import { useApolloClient } from '@apollo/client';
import {
  type ColumnFiltersState,
  type OnChangeFn,
  type PaginationState,
  type SortingState,
  type TableOptions,
  getCoreRowModel,
} from '@tanstack/react-table';
import { useDebounce } from '@uidotdev/usehooks';
import {
  type Parser,
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState,
  useQueryStates,
} from 'nuqs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  type DataTableFilterField,
  type ExtendedSortingState,
  getSortingStateParser,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { useMedplum } from '@eluve/medplum-client';

import {
  type ClinicalTaskRow,
  DEFAULT_TASK_PAGE_SIZE,
  type TaskViewerScope,
  buildClinicalTaskRows,
  fetchClinicalTasksPage,
  readTaskAppointmentId,
} from './fetch-clinical-task-rows';

const clinicalTaskAppointmentNamesQuery = graphql(`
  query clinicalTaskAppointmentNames($ids: [uuid!]!) {
    appointments(where: { id: { _in: $ids } }) {
      __typename
      id
      name
    }
  }
`);

const EMPTY_SORT: ExtendedSortingState<ClinicalTaskRow> = [];

export interface ClinicalTasksTableData {
  rows: ClinicalTaskRow[];
  /** Server match count (`Bundle.total`) — feeds the table's `rowCount`. */
  rowCount: number;
  /** manual-pagination/sorting/filtering options + URL-synced state, spread
   *  into the consumer's `useReactTable` (house `ServerDataTable` pattern). */
  reactTableOptions: Omit<TableOptions<ClinicalTaskRow>, 'data' | 'columns'>;
  isPending: boolean;
  isError: boolean;
  refetch: () => void;
  /** Live (undebounced) keyword — bind the search box's value to it. */
  search: string;
  /** Update the keyword; resets to page 1 (the fetch itself is debounced). */
  setSearch: (value: string) => void;
}

/**
 * Server-driven data source for the tenant Tasks table: pagination, sorting,
 * and faceted filtering all run browser-direct against Medplum FHIR search,
 * with page/perPage/sort/facet state mirrored to the URL (nuqs). A Medplum
 * analog of the house `useDataTableForQuery` (which is Apollo-only).
 *
 * Viewer scope follows the Medplum access level: `broad` (admins) sees every
 * clinical Task; `scoped-gp` (clinicians) sees the owner∪requester union,
 * folded into the query's `_filter`. `filterFields` must match the consuming
 * table's columns — their ids are the URL param keys.
 */
export function useClinicalTasksTableData({
  filterFields,
}: {
  filterFields: DataTableFilterField<ClinicalTaskRow>[];
}): ClinicalTasksTableData {
  const { client, enabled, access, practitionerId } = useMedplum();
  const apolloClient = useApolloClient();

  const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [perPage, setPerPage] = useQueryState(
    'perPage',
    parseAsInteger.withDefault(DEFAULT_TASK_PAGE_SIZE),
  );
  const [sorting, setSorting] = useQueryState(
    'sort',
    getSortingStateParser<ClinicalTaskRow>().withDefault(EMPTY_SORT),
  );
  const [search, setSearchParam] = useQueryState('search', parseAsString);
  // Debounce the server hit; the box stays responsive on the live value.
  const debouncedSearch = useDebounce(search, 300);

  const filterParsers = useMemo(
    () =>
      filterFields.reduce<Record<string, Parser<string[]>>>((acc, field) => {
        acc[field.id] = parseAsArrayOf(parseAsString, ',');
        return acc;
      }, {}),
    [filterFields],
  );
  const [filters, setFilters] = useQueryStates(filterParsers);

  const [rows, setRows] = useState<ClinicalTaskRow[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [isPending, setIsPending] = useState(true);
  const [isError, setIsError] = useState(false);
  const [fetchSeq, setFetchSeq] = useState(0);
  // A manual refetch (the edit sheet closing) refreshes in the background:
  // keep the current rows live instead of flipping the blocking `isPending`
  // state, which dims + disables the whole table for the fetch's duration.
  const backgroundRefetchRef = useRef(false);
  const refetch = useCallback(() => {
    backgroundRefetchRef.current = true;
    setFetchSeq((seq) => seq + 1);
  }, []);

  const scope = useMemo<TaskViewerScope | null>(() => {
    if (!access) return null;
    if (access.level === 'broad') return { level: 'broad' };
    if (!practitionerId) return null;
    return {
      level: 'scoped',
      practitionerReference: `Practitioner/${practitionerId}`,
    };
  }, [access, practitionerId]);

  // Read live sort/filter values inside the effect via refs, but re-run it on
  // their serialized identity — nuqs hands back fresh array/object refs each
  // render, which would otherwise loop the effect.
  const sortingRef = useRef(sorting);
  sortingRef.current = sorting;
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const sortKey = JSON.stringify(sorting);
  const filtersKey = JSON.stringify(filters);

  useEffect(() => {
    if (!enabled || !client || !scope) return;
    let cancelled = false;
    // Read-and-reset: the initial load and filter/sort/page changes block
    // (dim + disable) the table; a background refetch updates rows in place.
    const isBackground = backgroundRefetchRef.current;
    backgroundRefetchRef.current = false;
    if (!isBackground) {
      setIsPending(true);
      setIsError(false);
    }
    void (async () => {
      try {
        const { tasks, total } = await fetchClinicalTasksPage(client, {
          scope,
          offset: perPage * (page - 1),
          limit: perPage,
          sort: sortingRef.current,
          filters: filtersRef.current,
          search: debouncedSearch,
        });

        const appointmentIds = [
          ...new Set(
            tasks
              .map(readTaskAppointmentId)
              .filter((id): id is string => Boolean(id)),
          ),
        ];
        // Hasura enforces its own permissions here: appointments the viewer
        // can't read are simply absent, degrading those rows' sub-line/link.
        const appointmentNames = new Map<string, string>();
        if (appointmentIds.length > 0) {
          const result = await apolloClient.query({
            query: clinicalTaskAppointmentNamesQuery,
            variables: { ids: appointmentIds },
          });
          for (const appointment of result.data?.appointments ?? []) {
            appointmentNames.set(appointment.id, appointment.name);
          }
        }

        const nextRows = await buildClinicalTaskRows(
          client,
          tasks,
          appointmentNames,
        );
        if (cancelled) return;
        setRows(nextRows);
        setRowCount(total);
        setIsPending(false);
      } catch {
        if (cancelled) return;
        // A failed background refresh keeps the current rows rather than
        // replacing the table with the full error screen.
        if (!isBackground) {
          setIsError(true);
          setIsPending(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    client,
    scope,
    page,
    perPage,
    sortKey,
    filtersKey,
    debouncedSearch,
    apolloClient,
    fetchSeq,
  ]);

  const pagination: PaginationState = {
    pageIndex: page - 1,
    pageSize: perPage,
  };

  const columnFilters = useMemo<ColumnFiltersState>(
    () =>
      Object.entries(filters).flatMap(([id, value]) =>
        value && value.length ? [{ id, value }] : [],
      ),
    [filters],
  );

  const onPaginationChange = useCallback<OnChangeFn<PaginationState>>(
    (updater) => {
      const next =
        typeof updater === 'function' ? updater(pagination) : updater;
      void setPage(next.pageIndex + 1);
      void setPerPage(next.pageSize);
    },
    [pagination, setPage, setPerPage],
  );

  const onSortingChange = useCallback<OnChangeFn<SortingState>>(
    (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      void setSorting(next as ExtendedSortingState<ClinicalTaskRow>);
      void setPage(1);
    },
    [sorting, setSorting, setPage],
  );

  const onColumnFiltersChange = useCallback<OnChangeFn<ColumnFiltersState>>(
    (updater) => {
      const next =
        typeof updater === 'function' ? updater(columnFilters) : updater;
      const updates: Record<string, string[] | null> = {};
      for (const field of filterFields) updates[field.id] = null;
      for (const filter of next) updates[filter.id] = filter.value as string[];
      void setPage(1);
      void setFilters(updates);
    },
    [columnFilters, filterFields, setFilters, setPage],
  );

  const reactTableOptions = useMemo<
    Omit<TableOptions<ClinicalTaskRow>, 'data' | 'columns'>
  >(
    () => ({
      getCoreRowModel: getCoreRowModel(),
      manualPagination: true,
      manualSorting: true,
      manualFiltering: true,
      state: { pagination, sorting, columnFilters },
      onPaginationChange,
      onSortingChange,
      onColumnFiltersChange,
    }),
    [
      pagination,
      sorting,
      columnFilters,
      onPaginationChange,
      onSortingChange,
      onColumnFiltersChange,
    ],
  );

  const setSearch = useCallback(
    (value: string) => {
      // Empty string clears the URL param; typing jumps back to page 1.
      void setSearchParam(value || null);
      void setPage(1);
    },
    [setSearchParam, setPage],
  );

  return {
    rows,
    rowCount,
    reactTableOptions,
    isPending,
    isError,
    refetch,
    search: search ?? '',
    setSearch,
  };
}
